import { useQuery, useQueryClient } from "react-query";
import { api } from "utils/api";
import { ArtistReview, ProjectReview, ReviewResponse } from "../types";

const getReview = (
  entityType?: "ARTIST" | "PROJECT",
  entityId?: string,
  reviewedByUserId?: string
) => {
  if (!entityType || !entityId) {
    return Promise.reject(new Error("Invalid entity"));
  }
  if (!reviewedByUserId) {
    return Promise.reject(new Error("Invalid user id"));
  }

  return api.get<ArtistReview | ProjectReview>(
    `/${
      entityType === "ARTIST" ? "artists" : "projects"
    }/${entityId}/reviews/${reviewedByUserId}`
  );
};

const useReview = (
  entityType?: "ARTIST" | "PROJECT",
  entityId?: string,
  reviewedByUserId?: string
) => {
  const queryClient = useQueryClient();
  // Build query key for this review's parent entity
  const baseKey = ["reviews", entityType, entityId];
  // Find any cached reviews for this entity
  const cachedReviews = queryClient.getQueryData<ReviewResponse>(baseKey);

  return useQuery(
    [...baseKey, reviewedByUserId],
    () => getReview(entityType, entityId, reviewedByUserId),
    {
      enabled: !!(entityId && entityType && reviewedByUserId),
      // Use the cached review if it already exists in the list of cached reviews
      initialData: () =>
        cachedReviews?.data.find(
          (review) =>
            review.reviewableId === entityId &&
            review.reviewableType === entityType &&
            review.createdByUserId === reviewedByUserId
        ),
      // When fetched, also update this review in it's parent entity's list of reviews
      onSuccess: (newReview) => {
        if (cachedReviews) {
          // Find this review's index
          const index = cachedReviews.data?.findIndex(
            (review) =>
              review.reviewableId === entityId &&
              review.reviewableType === entityType &&
              review.createdByUserId === reviewedByUserId
          );
          // Return existing list with new data spliced into the array
          queryClient.setQueryData<ReviewResponse>(baseKey, (old) =>
            old
              ? {
                  ...old,
                  data: [
                    ...old.data.slice(0, index),
                    newReview,
                    ...old.data.slice(index + 1),
                  ],
                }
              : {
                  // Or if there was no list, return a new array
                  data: [newReview],
                }
          );
        }
      },
    }
  );
};

export { useReview };
